import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { inject, observer } from "mobx-react";
import ActiveAgentIcon from "../../components/ActiveAgentIcon";
import { useRunningConversations } from "../../llm/runningConversations";
import { SkeletonCardGrid, SkeletonRows } from "../../components/workspace/Skeleton";
import EmptyState from "../../components/workspace/EmptyState";
import WelcomeBanner from "./WelcomeBanner";
import NewProgramWizard from "./NewProgramWizard";
import { recentV2Conversations, countProjectChats } from "./ChatListRail";

const formatRelative = (iso) => {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  const delta = Date.now() - ts;
  const min = Math.floor(delta / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const ProgramCard = ({ program, onRename, onEditDescription, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(program.name || "");
  const [desc, setDesc] = useState(program.description || "");

  // Keep local fields synced when the parent reflows the program (e.g.
  // after another card committed a rename and the list re-rendered).
  useEffect(() => { setName(program.name || ""); setDesc(program.description || ""); }, [program.name, program.description]);

  const startEdit = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setEditing(true);
  };
  const cancelEdit = () => {
    setName(program.name || "");
    setDesc(program.description || "");
    setEditing(false);
  };
  const saveEdit = async () => {
    const nextName = (name || "").trim();
    const nameChanged = nextName && nextName !== program.name;
    const descChanged = desc !== (program.description || "");
    if (nameChanged) await onRename?.(program.id, nextName);
    if (descChanged) await onEditDescription?.(program.id, desc);
    setEditing(false);
  };

  if (editing) {
    return (
      <article
        className="home-card home-card--program home-card--edit-mode stone-card"
        data-testid={`v2-program-card-${program.id}`}
        onKeyDown={(e) => {
          if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEdit(); }
        }}
      >
        <input
          autoFocus
          className="home-card__title home-card__title-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Program name"
          data-testid={`v2-program-card-name-input-${program.id}`}
        />
        <textarea
          className="home-card__desc home-card__desc-input"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          rows={2}
          placeholder="Add a description…"
          data-testid={`v2-program-card-desc-input-${program.id}`}
        />
        <div className="home-card__meta">
          <span className="home-card__chip home-card__chip--program">Program</span>
          <span className="home-card__count">{program.projectCount} projects</span>
          <span className="home-card__count">{program.memberCount} members</span>
        </div>
        <footer className="home-card__footer home-card__footer--editing">
          <button
            type="button"
            className="home-card__cancel"
            onClick={cancelEdit}
            data-testid={`v2-program-card-cancel-${program.id}`}
          >
            Cancel
          </button>
          <button
            type="button"
            className="home-card__save"
            onClick={saveEdit}
            disabled={!(name || "").trim()}
            data-testid={`v2-program-card-save-${program.id}`}
          >
            Save
          </button>
        </footer>
      </article>
    );
  }

  return (
    <article
      className="home-card home-card--program stone-card"
      data-testid={`v2-program-card-${program.id}`}
    >
      <Link
        className="home-card__link-veil"
        to={`/v2/p/${program.id}`}
        aria-label={`Open ${program.name}`}
      />
      <div className="home-card__title" data-testid={`v2-program-card-name-${program.id}`}>
        {program.name}
      </div>
      <div className="home-card__desc" data-testid={`v2-program-card-desc-${program.id}`}>
        {program.description || <em className="home-card__desc--placeholder">No description</em>}
      </div>
      <div className="home-card__meta">
        <span className="home-card__chip home-card__chip--program">Program</span>
        <span className="home-card__count">{program.projectCount} projects</span>
        <span className="home-card__count">{program.memberCount} members</span>
        <span className="home-card__time">{formatRelative(program.lastActiveAt)}</span>
      </div>
      <footer className="home-card__footer">
        <button
          type="button"
          className="home-card__edit"
          onClick={startEdit}
          title="Edit name + description"
          data-testid={`v2-program-card-edit-${program.id}`}
        >
          ✎ Edit
        </button>
        <button
          type="button"
          className="home-card__delete"
          data-testid={`v2-program-card-delete-${program.id}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof window !== "undefined" && window.confirm(`Delete program "${program.name}"? Its projects will also be removed from the workspace.`)) {
              onDelete?.(program.id);
            }
          }}
        >
          ✕ Delete
        </button>
      </footer>
    </article>
  );
};

// `runningIds`: chats with an agent still working, which get the icon (PLEX-6361). A chat with
// `unread` finished while nobody was watching, and gets a dot (PLEX-6253).
export const RecentChatsList = ({ chats, emptyHint, runningIds = null }) => {
  if (!chats || chats.length === 0) {
    return <div className="recent__empty">{emptyHint || "No recent chats."}</div>;
  }
  return (
    <ul className="recent" data-testid="v2-recent-chats">
      {chats.map((chat) => (
        <li key={chat.id} className="recent__item">
          <Link
            to={`/v2/p/${chat.programId}/${chat.projectId}/c/${chat.id}`}
            className="recent__link"
            data-testid={`v2-recent-chat-${chat.id}`}
          >
            <div className="recent__title">
              {runningIds?.has(chat.id) && <ActiveAgentIcon />}
              {chat.unread && <span className="recent__unread" title="Finished while you were away" />}
              {chat.title}
            </div>
            <div className="recent__snippet">{chat.snippet}</div>
            <div className="recent__meta">
              <span className="recent__where">{chat.programId} · {chat.projectId}</span>
              <span className="recent__time">{formatRelative(chat.lastActiveAt)}</span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
};

const WorkspaceHomeBase = ({ workspaceStore, userStore, historyStore, tutorialStore }) => {
  // Wait for the Cognito bearer token before firing any /api/* requests.
  // Without this guard the first useEffect tick may fire with authData=null,
  // sending requests without an Authorization header → 401 in the console.
  const authReady = !!userStore?.authData;
  const reloadHistory = useCallback(() => {
    historyStore?.loadHistory?.("CONVERSATION").catch(() => {});
  }, [historyStore]);
  const runningChats = useRunningConversations(historyStore?.conversations,
                                               { auth: userStore?.authData, onEnded: reloadHistory });

  useEffect(() => {
    if (!authReady) return;
    workspaceStore.loadPrograms();
    // Pull v2-tagged conversations from history so the Recent chats
    // sidebar reflects real activity (the fixture path was returning
    // mock conversations that don't belong to any real program). On every
    // visit, not only the first: a chat left while its agent works must
    // show as running (PLEX-6361).
    reloadHistory();
  }, [workspaceStore, authReady, reloadHistory]);

  const [wizardOpen, setWizardOpen] = useState(false);

  // "Show welcome" in the account menu clears the localStorage flag and fires
  // this event; bump a nonce so the banner remounts with forceShow even if the
  // user was already on the workspace home.
  const [welcomeNonce, setWelcomeNonce] = useState(0);
  useEffect(() => {
    const handler = () => setWelcomeNonce((n) => n + 1);
    window.addEventListener("workspace:show-welcome", handler);
    return () => window.removeEventListener("workspace:show-welcome", handler);
  }, []);

  const { programs, programsLoading, programsError } = workspaceStore;
  const recentChats = recentV2Conversations(historyStore?.conversations || [], { limit: 8 });
  // A reload keeps the cached list on screen; the skeleton is for a first load only.
  const recentChatsLoading = !!historyStore?.loading && recentChats.length === 0;
  const showPrograms = !programsLoading && programs.length > 0;
  const showEmpty = !programsLoading && programs.length === 0 && !programsError;

  return (
    <section className="home stone-page" data-testid="v2-workspace-home">
      <div className="home__col home__col--main">
        <WelcomeBanner
          key={welcomeNonce}
          forceShow={welcomeNonce > 0}
          onStartTour={() => tutorialStore?.startContextTutorial("v2-workspace")}
        />
        <div className="home__heading-row">
          <h1 className="home__title hero-display">Workspace</h1>
          <button
            type="button"
            className="home__cta"
            onClick={() => setWizardOpen(true)}
            data-testid="v2-workspace-new-program-btn"
          >
            + New program
          </button>
        </div>
        <p className="home__sub">Programs you're a member of.</p>

        {programsLoading && <SkeletonCardGrid count={3} />}
        {programsError && <div className="home__error" data-testid="v2-workspace-error">{programsError}</div>}

        {showEmpty && (
          <EmptyState
            icon="🧬"
            title="No programs yet"
            body="Programs are the long-lived R&D containers that hold projects and chats. Create your first one to start chatting with Plex."
            action={{
              label: "+ New program",
              onClick: () => setWizardOpen(true),
              kind: "primary",
            }}
            testId="v2-workspace-empty"
          />
        )}

        {showPrograms && (
          <div className="home-grid" data-testid="v2-program-grid">
            {programs.map((p) => (
              <ProgramCard
                key={p.id}
                program={p}
                onRename={(id, name) => workspaceStore.updateProgram(id, { name }).catch((e) => console.error(e))}
                onEditDescription={(id, description) => workspaceStore.updateProgram(id, { description }).catch((e) => console.error(e))}
                onDelete={(id) => workspaceStore.deleteProgram(id).catch((e) => console.error(e))}
              />
            ))}
          </div>
        )}
      </div>

      <aside className="home__col home__col--side">
        <h2 className="home__sidetitle">Recent chats</h2>
        {recentChatsLoading
          ? <SkeletonRows count={3} />
          : <RecentChatsList chats={recentChats} runningIds={runningChats}
                             emptyHint="No recent chats across your programs." />
        }
      </aside>

      <NewProgramWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
      />
    </section>
  );
};

const WorkspaceHome = inject("workspaceStore", "userStore", "historyStore", "tutorialStore")(observer(WorkspaceHomeBase));
export default WorkspaceHome;
